import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as api from "./electricalApi";
import ElectricalDraft from "./ElectricalDraft";
import { exportReportPdf, previewReportPdf } from "./report/exportElectricalPdf";
import { exportReportWord } from "./report/exportElectricalWord";
import { exportReportExcel } from "./report/exportElectricalExcel";
import "./ElectricalReport.css";

/**
 * The report screen. Ported from Csa/CsaReport.js, same shape — choose what
 * prints, write the narrative sections, draft, preview, export — now that
 * the backend carries the same report-composition schema Electrical as CSA
 * (elec_reports / elec_all_observations / elec_report_section_meta /
 * elec_report_issue, elec_draft_job / elec_draft_item — see
 * sql/06_elec_report.sql, 07_elec_elaborate.sql, 09–14).
 *
 * Differences from CSA, all in vocabulary rather than structure:
 *
 *   - Checklist is area > category, not pillar > sub_pillar > element — one
 *     tier shallower, so "By pillar" / "By sub-pillar" becomes "By area" /
 *     "By category" and there is no element column anywhere.
 *   - No verification_method field — Electrical's checklist carries no
 *     equivalent, so the register is one column narrower.
 *   - The free-text field a draft reads from is `auditor_observation`, not
 *     `auditor_comment` (elec_answer.observation, not csa_answer.comment).
 */

const PRESETS = [
  { key: "findings", label: "Yes only", hint: "Checkpoints answered Yes — the default" },
  { key: "gaps_only", label: "Non-conformances", hint: "No only" },
  { key: "everything", label: "Everything", hint: "Every checkpoint, regardless of status" },
  { key: "none", label: "Clear", hint: "Start from nothing" },
];

const STATUS_ORDER = { NO: 0, Partial: 1, PARTIAL: 1, YES: 2, NA: 3, "N/A": 3 };

/** Which codes a preset selects, computed over this audit's own checkpoints. */
function presetCodes(items, key) {
  if (key === "everything") return new Set(items.map((i) => i.code));
  if (key === "none") return new Set();
  // Compared case-insensitively - the status actually on file is whatever
  // ElectricalQuestionCard's buttons sent (STATUS_OPTIONS is upper-case
  // today), but this is the one place a casing drift would silently empty
  // the default selection rather than throw.
  const status = (i) => String(i.status || "").toUpperCase();
  if (key === "gaps_only") return new Set(items.filter((i) => status(i) === "NO").map((i) => i.code));
  // findings (default): only a clean Yes prints; anything else is excluded.
  return new Set(items.filter((i) => status(i) === "YES").map((i) => i.code));
}

/**
 * The two layouts. Same data, different level of detail — neither is a
 * different report.
 *
 * Excel is absent on purpose: a spreadsheet is always the register, because
 * Elaborate is a print-layout idea that does not translate to rows a client
 * filters and sorts.
 */
const LAYOUT_CHOICES = [
  { key: "standard", label: "Standard", hint: "One row per finding · ~7 a page · long reports" },
  { key: "elaborate", label: "Elaborate", hint: "Each finding in full · ~4 a page · short reports" },
];

const FINDINGS_COLUMNS = [
  { key: "sr_no", label: "#", width: 4 },
  { key: "area", label: "Area", width: 14 },
  { key: "category", label: "Category", width: 14 },
  { key: "status", label: "Status", width: 8 },
  { key: "observation", label: "Observation", width: 28 },
  { key: "recommendation", label: "Recommendation", width: 14 },
  { key: "legal_reference", label: "Legal reference", width: 10 },
  { key: "score", label: "Score", width: 4 },
  { key: "photos", label: "Evidence", width: 4 },
];

/**
 * Preview and export's own model — built from data this screen already has
 * (the checklist, the answers, the rollup from getAudit()), not from the
 * server's getReportModel composer's findings/scoring, which isn't proven
 * live (see doPreview's history). ElectricalStandardPdf and
 * ElectricalElaboratePdf only ever read `model.header` / `.summary` /
 * `.sections`, so a model with just the findings section is a smaller but
 * still genuinely renderable document — both the register (`data.rows`) and
 * the grouped tree (`data.areas`) are included, so either layout's renderer
 * finds what it needs.
 *
 * `narrativeModel` is the *other* model - the one `loadModel()` already
 * fetches for the Narrative tab, which that tab has proven actually carries
 * real saved text (typing there autosaves and survives reload - see
 * saveReportSection). Its narrative sections are spliced in here so PDF and
 * Word print the same prose the Narrative tab shows, same as CSA's exporters
 * do. Excel never gets them - CSA's own exportExcel.js has no narrative
 * sheet either, because a spreadsheet is the register, not prose.
 */
function buildLocalReportModel({ items, selected, audit, organization, rollup, layout, narrativeModel }) {
  const rows = items
    .filter((i) => selected.has(i.code))
    .slice()
    .sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) || a.sr_no - b.sr_no)
    .map((r, i) => ({
      number: i + 1,
      code: r.code,
      sr_no: r.sr_no,
      area: r.area,
      category: r.category,
      status: r.status || "—",
      status_label: r.status || "Not answered",
      observation: r.observation || r.checkpoint,
      statement: r.checkpoint,
      note: r.observation,
      recommendation: null,
      legal_reference: null,
      score: null,
      max_pts: null,
      photos: [],
    }));

  const areaMap = new Map();
  rows.forEach((r) => {
    if (!areaMap.has(r.area)) areaMap.set(r.area, { name: r.area, categories: new Map() });
    const cats = areaMap.get(r.area).categories;
    if (!cats.has(r.category)) cats.set(r.category, { name: r.category, findings: [] });
    cats.get(r.category).findings.push(r);
  });
  const areas = [...areaMap.values()].map((a) => ({
    name: a.name,
    categories: [...a.categories.values()],
  }));

  const overall = rollup && rollup.overall ? rollup.overall : null;
  const assessed = items.filter((i) => i.status).length;

  // Only the body text is trusted from the narrative model - its own
  // findings/score sections are what Preview stopped relying on in the
  // first place, so this takes nothing else from it. A section nobody has
  // written yet (body null/blank) is left out entirely rather than printed
  // as an empty heading or a "not written" placeholder - the document
  // should read as if that section was never offered, not as if it was
  // asked for and skipped.
  const narrativeSections = ((narrativeModel && narrativeModel.sections) || [])
    .filter((s) => s.kind === "narrative" && String(s.body || "").trim())
    .map((s, i) => ({
      key: s.key,
      // Falls back to an ordinal before findings (section 7) rather than
      // trusting `.no` to exist - an undefined `.no` would satisfy neither
      // `before`/`after`'s `< 7` / `> 7` test and the section would silently
      // never render at all.
      no: Number.isFinite(s.no) ? s.no : i + 1,
      kind: "narrative",
      title: s.title || s.key,
      body: s.body,
    }));

  const findingsSection = {
    key: "findings",
    no: 7,
    kind: "data",
    title: "Detailed Findings",
    data: {
      columns: FINDINGS_COLUMNS,
      rows,
      areas,
      selected: rows.length,
      assessed,
    },
  };

  return {
    layout,
    header: {
      organization: (organization && organization.org_name) || "",
      site: (audit && audit.site_name) || "",
      report_id: (audit && audit.report_id) || "",
      framework_version: (audit && audit.framework_version) || "",
      start_date: audit && audit.start_date,
      end_date: audit && audit.end_date,
      prepared_by: (audit && audit.auditor_name) || "",
      reviewed_by: (audit && audit.reviewed_by_name) || "",
      was_reviewed: !!(audit && audit.reviewed_at),
    },
    summary: {
      overall_pct: overall ? overall.pct : null,
      maturity_band: overall ? overall.band : null,
      counts: { assessed, total: items.length, selected: rows.length },
    },
    sections: [...narrativeSections, findingsSection].sort((a, b) => a.no - b.no),
  };
}

export default function ElectricalReport({ auditId, audit }) {
  const [tab, setTab] = useState("contents");
  const [layout, setLayout] = useState("standard");
  /*
   * Off by default. A recommendation is written as a corrective action, so
   * printed beside a Yes it reads as a criticism of something that passed —
   * which is why the projection never stored one there.
   */
  const [conformantRecs, setConformantRecs] = useState(false);
  const [model, setModel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [preview, setPreview] = useState(null);
  const [issues, setIssues] = useState(null);
  const previewUrl = useRef(null);

  const notReady = !audit || audit.status === "draft";

  /*
   * "What prints" is built from data this audit already has — the
   * framework's own checkpoints and the answers actually saved against
   * them — rather than a composed report-contents endpoint. Every
   * checkpoint this audit has an answer for appears here, same as every
   * question appears in CSA's Contents list; there is no server-side
   * selection to round-trip yet, so which ones will print is tracked
   * locally below.
   */
  const [framework, setFramework] = useState(null);
  // getAudit's own checkpoint list — this audit may be scoped to only some
  // of the framework's areas/categories (chosen on the new-audit form), same
  // field ElectricalAudit.js reads to build its own scopedFramework. The
  // report must not print a category nobody selected for this audit.
  const [checkpointsChk, setCheckpointsChk] = useState(null);
  const [answers, setAnswers] = useState({});
  const [photos, setPhotos] = useState([]);
  // organization + rollup come off the same getAudit() call ElectricalAudit.js
  // already relies on — real, working data, used below to build the preview
  // model locally instead of the not-yet-live getReportModel endpoint.
  const [organization, setOrganization] = useState(null);
  const [rollup, setRollup] = useState(null);

  const loadChecklist = useCallback(() => {
    if (notReady) return;
    Promise.all([api.getFramework(), api.getAudit(auditId), api.listPhotos(auditId)])
      .then(([fw, data, ph]) => {
        setFramework(fw);
        setCheckpointsChk(data?.checkpoints_chk || null);
        setOrganization(data?.organization || null);
        setRollup(data?.summary || null);
        const byCode = {};
        (data?.answers || []).forEach((a) => {
          byCode[a.checkpoint_code] = a;
        });
        setAnswers(byCode);
        setPhotos(ph || []);
      })
      .catch((e) => setError(e.message));
  }, [auditId, notReady]);

  const loadModel = useCallback(() => {
    if (notReady) return;
    api
      .getReportModel(auditId, { layout, conformantRecs })
      .then((m) => {
        setModel(m);
        setError(null);
      })
      .catch((e) => setError(e.message));
  }, [auditId, notReady, layout, conformantRecs]);

  const loadIssues = useCallback(() => {
    if (notReady) return;
    api
      .listReportIssues(auditId)
      .then(setIssues)
      .catch(() => setIssues(null));
  }, [auditId, notReady]);

  useEffect(loadChecklist, [loadChecklist]);
  useEffect(loadModel, [loadModel]);
  useEffect(loadIssues, [loadIssues]);

  /**
   * This audit's own checkpoint set, from getAudit's `checkpoints_chk`.
   * Falls back to null — "no scoping info, show the full framework" —
   * rather than an empty Set, so a missing or differently-shaped field
   * never silently blanks the whole report.
   */
  const scopedCodes = useMemo(() => {
    if (!checkpointsChk || !checkpointsChk.length) return null;
    const codes = checkpointsChk
      .map((c) => String(c.checkpoint_code ?? c.code ?? "").trim())
      .filter(Boolean);
    return codes.length ? new Set(codes) : null;
  }, [checkpointsChk]);

  /** The framework, filtered down to only the areas/categories chosen for this audit. */
  const scopedFramework = useMemo(() => {
    if (!framework) return framework;
    if (!scopedCodes) return framework;

    const areas = framework.areas
      .map((a) => {
        const categories = a.categories
          .map((cat) => ({
            ...cat,
            checkpoints: cat.checkpoints.filter((cp) => scopedCodes.has(cp.code)),
          }))
          .filter((cat) => cat.checkpoints.length);
        return { ...a, categories };
      })
      .filter((a) => a.categories.length);

    return { ...framework, areas };
  }, [framework, scopedCodes]);

  /** This audit's checkpoints, flattened and stamped with the answer actually on file for each. */
  const items = useMemo(() => {
    if (!scopedFramework) return [];
    const out = [];
    let sr = 0;
    (scopedFramework.areas || []).forEach((a) => {
      (a.categories || []).forEach((cat) => {
        (cat.checkpoints || []).forEach((cp) => {
          const ans = answers[cp.code] || {};
          sr += 1;
          out.push({
            id: cp.code,
            code: cp.code,
            sr_no: sr,
            area: a.area,
            category: cat.category,
            checkpoint: cp.check_points,
            criticality: cp.criticality,
            status: ans.status || null,
            observation: ans.observation || cp.observation || "",
            has_photos: photos.some((p) => (p.checkpoint_codes || []).includes(cp.code)),
          });
        });
      });
    });
    return out;
  }, [scopedFramework, answers, photos]);

  const [selected, setSelected] = useState(() => new Set());
  const seeded = useRef(false);

  // Seeded once the checklist has actually loaded — not before, or the
  // default preset would compute over an empty list and select nothing.
  useEffect(() => {
    if (seeded.current || !items.length) return;
    seeded.current = true;
    setSelected(presetCodes(items, "findings"));
  }, [items]);

  const contents = useMemo(
    () => ({
      items: items.map((i) => ({ ...i, is_selected: selected.has(i.code) })),
      selected: selected.size,
      total: items.length,
    }),
    [items, selected]
  );

  // Blob URLs are not collected while the document lives.
  useEffect(
    () => () => {
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    },
    []
  );

  const flash = (m) => {
    setNote(m);
    setTimeout(() => setNote(null), 4000);
  };

  const applyPreset = (key) => {
    const next = presetCodes(items, key);
    setSelected(next);
    flash(`${next.size} of ${items.length} will print.`);
  };

  const toggle = (item) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  };

  const saveSection = async (key, body) => {
    try {
      await api.saveReportSection(auditId, key, body);
      loadModel();
    } catch (e) {
      setError(e.message);
    }
  };

  /**
   * Draft one narrative section with AI, from this audit's own findings.
   * Returns the generated body so the box can show it immediately, rather
   * than waiting on the next loadModel() round trip - loadModel() still
   * runs after, to pick up the server's is_ai_drafted/written_by stamp.
   */
  const draftSection = async (key) => {
    const r = await api.draftReportSection(auditId, key);
    loadModel();
    return r;
  };

  const doPreview = async () => {
    setBusy(true);
    setError(null);
    try {
      const m = buildLocalReportModel({
        items, selected, audit, organization, rollup, layout, narrativeModel: model,
      });
      const { url, bytes } = await previewReportPdf(m, { photoBytes: api.photoBytes });
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
      previewUrl.current = url;
      setPreview({ url, bytes });
      setTab("preview");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * All three formats are the same call with a different renderer — the
   * model is fetched once and drawn, never rebuilt per format. That is what
   * stops a client's PDF and spreadsheet disagreeing.
   */
  const EXPORTERS = {
    pdf: exportReportPdf,
    word: exportReportWord,
    excel: exportReportExcel,
  };

  /**
   * Exporting, like Preview, draws the model built locally — same reasoning
   * as doPreview: openReportIssue is the same kind of not-proven-live
   * endpoint getReportModel turned out to be, and a PDF/Word file that
   * matches what Preview just showed beats one that depends on a call that
   * may not exist yet. The one thing genuinely lost by not calling it is the
   * allocated issue number stamped into the footer and the issue-history
   * row - there is no way to get either without a server action, so this
   * exports an un-numbered document rather than failing outright.
   */
  const doExport = async (format) => {
    setBusy(true);
    setError(null);
    try {
      const m = buildLocalReportModel({
        items, selected, audit, organization, rollup, layout, narrativeModel: model,
      });
      const out = await EXPORTERS[format](m, { photoBytes: api.photoBytes });
      flash(`${out.name} — ${Math.round(out.bytes / 1024)} KB` + (out.photos ? ` · ${out.photos}` : ""));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (notReady) {
    return (
      <div className="cap-writeup">
        <div className="wu-empty">
          <h2>No report yet</h2>
          <p>
            The report is built from the submitted audit. Finish and submit
            first — the score is fixed at that moment, and everything the
            client sees comes from it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cap-writeup rep">
      <div className="wu-head">
        <div>
          <h2>Report</h2>
          <p>
            {items.length
              ? `${selected.size} of ${items.length} checkpoints will print · ${layout} layout`
              : "Loading…"}
          </p>
        </div>
        <div className="rep-actions">
          <button type="button" className="small-btn" disabled={busy} onClick={doPreview}>
            {busy ? "Working…" : "Preview"}
          </button>
          <button
            type="button"
            className="cap-btn"
            disabled={busy || !items.length}
            onClick={() => doExport("pdf")}
          >
            PDF
          </button>
          <button
            type="button"
            className="small-btn"
            disabled={busy || !items.length}
            onClick={() => doExport("word")}
            title="Editable, for pasting into a corrective-action tracker"
          >
            Word
          </button>
          <button
            type="button"
            className="small-btn"
            disabled={busy || !items.length}
            onClick={() => doExport("excel")}
            title="The register, filterable and sortable"
          >
            Excel
          </button>
        </div>
      </div>

      {error ? <p className="wu-error">{error}</p> : null}
      {note ? <p className="rep-note">{note}</p> : null}

      <div className="rep-layouts">
        {LAYOUT_CHOICES.map((l) => (
          <button
            key={l.key}
            type="button"
            className={"rep-layout" + (layout === l.key ? " on" : "")}
            onClick={() => setLayout(l.key)}
          >
            <strong>{l.label}</strong>
            <span>{l.hint}</span>
          </button>
        ))}
      </div>

      <label className="rep-opt">
        <input
          type="checkbox"
          checked={conformantRecs}
          onChange={(e) => setConformantRecs(e.target.checked)}
        />
        <span>
          <strong>Show recommendations against conformant findings</strong>
          <em>
            Off by default. A recommendation is written as a corrective action, so beside a
            Yes it reads as a fault rather than the standard being met. Turn this on where a
            client wants the requirement shown against every checkpoint.
          </em>
        </span>
      </label>

      <nav className="rep-tabs">
        {[
          { k: "contents", label: "What prints" },
          { k: "sections", label: "Narrative" },
          { k: "draft", label: "Drafting" },
          { k: "preview", label: "Preview" },
          { k: "issues", label: issues && issues.total ? `Issues (${issues.total})` : "Issues" },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            className={"rep-tab" + (tab === t.k ? " on" : "")}
            onClick={() => setTab(t.k)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "contents" ? (
        <Contents
          contents={contents}
          busy={busy}
          onPreset={applyPreset}
          onToggle={toggle}
        />
      ) : null}

      {tab === "sections" ? (
        <Sections model={model} onSave={saveSection} onDraft={draftSection} />
      ) : null}

      {tab === "draft" ? <ElectricalDraft auditId={auditId} audit={audit} /> : null}

      {tab === "issues" ? <Issues issues={issues} /> : null}

      {tab === "preview" ? (
        preview ? (
          <div className="rep-preview">
            <p className="rep-note">
              {Math.round(preview.bytes / 1024)} KB. A preview is not an issue — it carries no
              issue number and nothing is recorded. Use PDF, Word or Excel to issue it.
            </p>
            <iframe title="Report preview" src={preview.url} />
          </div>
        ) : (
          <div className="wu-empty">
            <h2>Nothing rendered yet</h2>
            <p>Choose what prints, then press Preview.</p>
          </div>
        )
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Contents({ contents, busy, onPreset, onToggle }) {
  const [filter, setFilter] = useState("all");

  if (!contents) return <div className="cap-loading">Loading…</div>;

  const items = contents.items
    .filter((i) =>
      filter === "all" ? true : filter === "in" ? i.is_selected : !i.is_selected
    )
    .slice()
    .sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) || a.sr_no - b.sr_no);

  return (
    <>
      <div className="rep-presets">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            className="rep-preset"
            disabled={busy}
            onClick={() => onPreset(p.key)}
          >
            <strong>{p.label}</strong>
            <span>{p.hint}</span>
          </button>
        ))}
      </div>

      <div className="rep-bar">
        <span>
          <strong>{contents.selected}</strong> of {contents.total} selected
        </span>
        <div className="rep-filters">
          {[
            { k: "all", label: "All" },
            { k: "in", label: "Printing" },
            { k: "out", label: "Excluded" },
          ].map((f) => (
            <button
              key={f.k}
              type="button"
              className={"rep-filter" + (filter === f.k ? " on" : "")}
              onClick={() => setFilter(f.k)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rep-list">
        {items.map((i) => (
          <label key={i.id} className={"rep-item" + (i.is_selected ? " on" : "")}>
            <input type="checkbox" checked={i.is_selected} onChange={() => onToggle(i)} />
            <span className={"rep-status s-" + String(i.status).toLowerCase().replace("/", "")}>
              {i.status}
            </span>
            <span className="rep-where">
              {i.area} · {i.category}
            </span>
            <span className="rep-obs">{i.observation}</span>
            {i.has_photos ? <span className="rep-cam">photo</span> : null}
          </label>
        ))}
        {items.length === 0 ? <p className="rep-none">Nothing here.</p> : null}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

/**
 * The narrative sections AI can draft — the elec_prompt rows actually seeded
 * (sql/11_elec_prompt_seed.sql). Any other narrative section (Introduction,
 * say, if the composer has one) has no prompt behind it yet, so it gets no
 * button rather than one that always fails.
 */
const AI_DRAFTABLE_SECTIONS = new Set([
  "executive_summary",
  "critical_observations",
  "way_forward",
  "conclusion",
]);

function Sections({ model, onSave, onDraft }) {
  if (!model) return <div className="cap-loading">Loading…</div>;
  const narrative = model.sections.filter((s) => s.kind === "narrative");

  return (
    <div className="rep-sections">
      <p className="rep-hint">
        The generated sections (score analysis, overall assessment, scope) are
        computed from the audit and cannot be edited — a report should not be
        able to state a figure the data does not support.
      </p>
      {narrative.map((s) => (
        <SectionEditor key={s.key} section={s} onSave={onSave} onDraft={onDraft} />
      ))}
    </div>
  );
}

function SectionEditor({ section, onSave, onDraft }) {
  const [text, setText] = useState(section.body || "");
  const [saved, setSaved] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState(null);
  const timer = useRef(null);
  const dirty = useRef(false);

  useEffect(() => {
    if (!dirty.current) setText(section.body || "");
  }, [section.body]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const type = (e) => {
    const v = e.target.value;
    setText(v);
    dirty.current = true;
    setSaved(false);
    setDraftError(null);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      dirty.current = false;
      onSave(section.key, v);
      setSaved(true);
    }, 900);
  };

  const draft = async () => {
    clearTimeout(timer.current);
    setDrafting(true);
    setDraftError(null);
    try {
      const r = await onDraft(section.key);
      // The server already saved this as the section's body - the box just
      // needs to show it, not re-send it through onSave.
      dirty.current = false;
      setText(r && r.body ? r.body : "");
      setSaved(true);
    } catch (e) {
      setDraftError(e.message || "Could not draft this section");
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className="rep-section">
      <div className="rep-section-head">
        <h3>
          {section.no}. {section.title}
        </h3>
        {saved ? <span className="rep-saved">Saved</span> : null}
        {AI_DRAFTABLE_SECTIONS.has(section.key) ? (
          <button
            type="button"
            className="small-btn"
            style={{ marginLeft: "auto" }}
            disabled={drafting}
            onClick={draft}
          >
            {drafting ? "Drafting…" : "Draft with AI"}
          </button>
        ) : null}
      </div>
      {draftError ? <p className="wu-error">{draftError}</p> : null}
      <textarea
        rows={6}
        value={text}
        placeholder="Leave blank to omit this section's text. Separate paragraphs with a blank line."
        onChange={type}
        onBlur={() => {
          if (!dirty.current) return;
          clearTimeout(timer.current);
          dirty.current = false;
          onSave(section.key, text);
          setSaved(true);
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * What has actually gone out.
 *
 * Selection stays editable after a report is issued, so the same audit can
 * produce several documents. This is how someone answers "which one are they
 * holding?" — and why the checksum is shown: it is over the composed model,
 * not the file, so two exports of identical content match even though their
 * bytes differ.
 */
function Issues({ issues }) {
  if (!issues) return <div className="cap-loading">Loading…</div>;

  if (!issues.total) {
    return (
      <div className="wu-empty">
        <h2>Nothing issued yet</h2>
        <p>
          Every export records an issue — a number, a date, and exactly what was in it. The
          document carries the same line in its footer, so a client can always say which
          version they have.
        </p>
      </div>
    );
  }

  return (
    <div className="rep-issues">
      <p className="rep-hint">
        Scores never change between issues — they were fixed when the audit was submitted.
        Only the level of detail and the selection differ.
      </p>

      {issues.issues.map((i) => (
        <div className="rep-issue" key={i.issue_no}>
          <div className="rep-issue-no">{i.issue_no}</div>
          <div className="rep-issue-main">
            <div className="rep-issue-top">
              <strong>
                {new Date(i.exported_at).toLocaleString(undefined, {
                  day: "numeric", month: "long", year: "numeric",
                  hour: "2-digit", minute: "2-digit",
                })}
              </strong>
              <span className="rep-issue-fmt">{i.file_format}</span>
              <span className="rep-issue-fmt">{i.layout}</span>
            </div>
            <div className="rep-issue-sub">
              {i.selected_count} of {i.assessed_count} assessed findings reported
              {i.exported_by ? ` · ${i.exported_by}` : ""}
            </div>
            <div className="rep-issue-sum" title="Fingerprint of the content, not the file">
              {String(i.checksum || "").slice(0, 16)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
